import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BookOpen, Calculator, Microscope, Star, Brain } from 'lucide-react';
import { useActivities } from '../../hooks/useActivities';
import { useProgress } from '../../hooks/useProgress';
import { useChildStats } from '../../hooks/useChildStats';
import { useAIActivities } from '../../hooks/useAIActivities';
import { ActivityCard } from '../../components/ActivityCard';
import { AIActivityCard } from '../../components/AIActivityCard';
import { ActivitySimulator } from '../../components/ActivitySimulator';
import { AIActivitySimulator } from '../../components/AIActivitySimulator';

interface ChildContextType {
  selectedChild: string;
  selectedChildData: any;
  soundEnabled: boolean;
  isDarkMode: boolean;
  theme: any;
}

export function ChildActivitiesPage() {
  const { selectedChild, selectedChildData, soundEnabled, isDarkMode, theme } = useOutletContext<ChildContextType>();
  const { activities } = useActivities();
  const { addProgress } = useProgress(selectedChild);
  const { stats, refetch: refetchStats } = useChildStats(selectedChild);
  const { aiActivities, markAsCompleted } = useAIActivities(selectedChild);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [showActivity, setShowActivity] = useState<string | null>(null);
  const [showAIActivity, setShowAIActivity] = useState<string | null>(null);

  const subjects = [
    { id: 'all', name: 'All Activities', icon: Star, color: 'rainbow' },
    { id: 'math', name: 'Math', icon: Calculator, color: 'blue' },
    { id: 'reading', name: 'Reading', icon: BookOpen, color: 'green' },
    { id: 'science', name: 'Science', icon: Microscope, color: 'purple' },
  ];

  const filteredActivities = selectedSubject === 'all' 
    ? activities 
    : activities.filter(activity => activity.subject === selectedSubject);

  const filteredAIActivities = selectedSubject === 'all'
    ? aiActivities
    : aiActivities.filter(activity => activity.subject === selectedSubject);

  const handleActivityComplete = async (score: number, timeSpent: number) => {
    if (!showActivity) return;

    const activity = activities.find(a => a.id === showActivity);
    if (!activity) return;

    const pointsEarned = Math.floor((score / 100) * activity.points_reward);

    await addProgress({
      child_id: selectedChild,
      activity_id: showActivity,
      score,
      time_spent: timeSpent,
      points_earned: pointsEarned,
    });

    refetchStats();
    setShowActivity(null);
  };

  const handleAIActivityComplete = async (score: number, timeSpent: number) => {
    if (!showAIActivity) return;

    const aiActivity = aiActivities.find(a => a.id === showAIActivity);
    if (!aiActivity) return;

    const pointsEarned = Math.floor((score / 100) * aiActivity.points_reward);

    await markAsCompleted(showAIActivity);
    await addProgress({
      child_id: selectedChild,
      activity_id: showAIActivity,
      score,
      time_spent: timeSpent,
      points_earned: pointsEarned,
    });

    refetchStats();
    setShowAIActivity(null);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4 text-[var(--text-primary)] font-baloo">
          Learning Activities
        </h1>
        <p className="text-xl text-[var(--text-secondary)]">
          Choose what you want to learn today, {selectedChildData.name}!
        </p>
      </div>

      {/* Subject Filters */}
      <div className="flex flex-wrap justify-center gap-4">
        {subjects.map((subject) => (
          <button
            key={subject.id}
            onClick={() => setSelectedSubject(subject.id)}
            className={`flex items-center space-x-3 px-6 py-4 rounded-2xl font-bold text-lg transition-all duration-200 ${
              theme.reducedMotion ? '' : 'transform hover:scale-105'
            } ${
              selectedSubject === subject.id
                ? subject.color === 'rainbow'
                  ? 'bg-gradient-to-r from-pink-400 via-purple-500 to-blue-500 text-white shadow-lg'
                  : theme.mode === 'high-contrast'
                  ? `bg-[var(--accent-${subject.color})] text-black border-4 border-white shadow-lg`
                  : `bg-[var(--accent-${subject.color})] text-white shadow-lg`
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] border-2 border-[var(--border-color)] hover:border-[var(--accent-blue)] hover:text-[var(--text-primary)] shadow-md'
            } font-baloo`}
          >
            <subject.icon className="w-6 h-6" />
            <span>{subject.name}</span>
          </button>
        ))}
      </div>

      {/* AI Activities Section */}
      {filteredAIActivities.length > 0 && (
        <div>
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Brain className="w-8 h-8 text-purple-600" />
            <h2 className="text-3xl font-bold text-[var(--text-primary)] font-baloo">
              Special Activities Made for You!
            </h2>
            <Star className="w-6 h-6 text-yellow-500" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {filteredAIActivities.map((activity) => (
              <AIActivityCard
                key={activity.id}
                activity={activity}
                onStart={() => setShowAIActivity(activity.id)}
                completed={activity.status === 'completed'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Regular Activities */}
      <div>
        <h2 className="text-3xl font-bold text-center mb-6 text-[var(--text-primary)] font-baloo">
          {selectedSubject === 'all' ? 'All Activities' : subjects.find(s => s.id === selectedSubject)?.name}
        </h2>
        
        {filteredActivities.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 bg-[var(--bg-tertiary)]">
              <BookOpen className="w-12 h-12 text-[var(--text-muted)]" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[var(--text-primary)] font-baloo">
              No Activities Found
            </h3>
            <p className="text-lg text-[var(--text-secondary)]">
              Try selecting a different subject!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredActivities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                onStart={() => setShowActivity(activity.id)}
                completed={stats.completedActivityIds.includes(activity.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Activity Simulator Modal */}
      {showActivity && (
        <ActivitySimulator
          activity={activities.find(a => a.id === showActivity)!}
          onComplete={handleActivityComplete}
          onClose={() => setShowActivity(null)}
        />
      )}

      {/* AI Activity Simulator Modal */}
      {showAIActivity && (
        <AIActivitySimulator
          activity={aiActivities.find(a => a.id === showAIActivity)!}
          onComplete={handleAIActivityComplete}
          onClose={() => setShowAIActivity(null)}
        />
      )}
    </div>
  );
}